import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarDays, MessageSquare, Paperclip } from 'lucide-react';
import type { TaskSummary } from '@ssn/shared';
import { formatTaskReference } from '@ssn/shared';
import { StatusBadge, PriorityBadge } from './status';
import { UserChip } from '@/components/ui/Avatar';
import { Chip } from '@/components/ui/Badge';
import { TableWrap, Td, Th, Tr } from '@/components/ui/Table';
import { EmptyState, SkeletonRows } from '@/components/ui/Feedback';
import { cn } from '@/components/ui/cn';
import { formatDate, formatRelativeTime } from '@/lib/format';

export interface TaskTableProps {
  tasks: readonly TaskSummary[];
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  /** Hide the assignee column on a list that is already scoped to one person. */
  hideAssignee?: boolean;
  className?: string;
}

export function TaskTable({
  tasks,
  loading,
  emptyTitle = 'No tasks match these filters',
  emptyDescription = 'Try widening the filters, or clear them to see everything you have access to.',
  emptyAction,
  hideAssignee = false,
  className,
}: TaskTableProps) {
  if (loading && tasks.length === 0) {
    return <SkeletonRows rows={6} className="p-4" />;
  }

  if (tasks.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        {...(emptyAction ? { action: emptyAction } : {})}
      />
    );
  }

  return (
    <TableWrap className={cn(loading && 'opacity-60 transition-opacity', className)}>
      <thead>
        <tr>
          <Th className="w-20">Task</Th>
          <Th>Title</Th>
          <Th className="w-40">Status</Th>
          <Th className="w-28">Priority</Th>
          {!hideAssignee && <Th className="w-44">Developer</Th>}
          <Th className="w-32">Deadline</Th>
          <Th className="w-32" align="right">
            Updated
          </Th>
        </tr>
      </thead>
      <tbody>
        {tasks.map((task) => (
          <Tr key={task.id}>
            <Td className="font-mono text-xs text-text-subtle">
              <Link to={`/tasks/${task.reference}`} className="hover:text-accent-text hover:underline">
                {formatTaskReference(task.reference)}
              </Link>
            </Td>

            <Td>
              <Link to={`/tasks/${task.reference}`} className="group block min-w-0">
                <span className="block truncate font-medium text-text group-hover:text-accent-text">
                  {task.title}
                </span>
                <span className="mt-1 flex flex-wrap items-center gap-1.5">
                  {task.project && <Chip title="Project">{task.project.name}</Chip>}
                  {task.category && <Chip title="Category">{task.category.name}</Chip>}
                  {task.commentCount > 0 && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] text-text-subtle"
                      title={`${task.commentCount} comment${task.commentCount === 1 ? '' : 's'}`}
                    >
                      <MessageSquare aria-hidden className="size-3" />
                      {task.commentCount}
                    </span>
                  )}
                  {task.attachmentCount > 0 && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] text-text-subtle"
                      title={`${task.attachmentCount} attachment${task.attachmentCount === 1 ? '' : 's'}`}
                    >
                      <Paperclip aria-hidden className="size-3" />
                      {task.attachmentCount}
                    </span>
                  )}
                  {task.reviewCycles > 1 && (
                    <Chip title="Number of review rounds so far">{task.reviewCycles} review rounds</Chip>
                  )}
                </span>
              </Link>
            </Td>

            <Td>
              <StatusBadge status={task.status} size="sm" />
              {task.status === 'PENDING_FINAL_REVIEW' && task.metrics.currentWaitMs !== null && (
                <span className="mt-1 block text-[11px] text-text-subtle tabular">
                  waiting {formatRelativeTime(task.submittedForReviewAt).replace(' ago', '')}
                </span>
              )}
            </Td>

            <Td>
              <PriorityBadge priority={task.priority} size="sm" />
            </Td>

            {!hideAssignee && (
              <Td>
                <UserChip user={task.assignee} size="xs" />
              </Td>
            )}

            <Td>
              <DeadlineCell task={task} />
            </Td>

            <Td align="right">
              <span className="text-xs text-text-muted" title={task.updatedAt}>
                {formatRelativeTime(task.updatedAt)}
              </span>
            </Td>
          </Tr>
        ))}
      </tbody>
    </TableWrap>
  );
}

/**
 * A deadline is only interesting when it is close or missed, so an overdue task
 * says so in words and with an icon — not by turning the date red and hoping.
 */
export function DeadlineCell({ task }: { task: TaskSummary }) {
  if (!task.deadline) return <span className="text-xs text-text-subtle">—</span>;

  if (task.metrics.isOverdue) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-critical-tone" title={task.deadline}>
        <AlertTriangle aria-hidden className="size-3.5" />
        Overdue
        <span className="font-normal text-text-muted">· {formatDate(task.deadline)}</span>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-xs text-text-muted" title={task.deadline}>
      <CalendarDays aria-hidden className="size-3.5" />
      {formatDate(task.deadline)}
    </span>
  );
}
